import React, { useCallback, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { ViewportList } from 'react-viewport-list'

import { type ChatId, useSelectedChatKey } from '../../blocs/chatVisibility'
import { ChatMessage } from '../ChatMessage/ChatMessage'
import {
  getNextPage,
  isFetchingNextPage,
  useChatMessages,
  useHasNextPage,
} from '../../blocs/useChat'

import { usePinScrollToBottom } from './usePinScrollToBottom'

import './ChatMessages.scss'

interface ChatMessagesProps {
  type: ChatId
}

const ChatMessages = ({ type }: ChatMessagesProps) => {
  const chatMessageIds = useChatMessages(type)
  const canFetchNextPage = useHasNextPage(type)

  const scrollContainer = useRef<HTMLDivElement>(null)
  usePinScrollToBottom(scrollContainer, chatMessageIds?.length)

  const isHidden = useSelectedChatKey() !== type

  const handleScroll = useCallback(
    (items) => {
      if (!chatMessageIds) return
      if (!canFetchNextPage || isFetchingNextPage(type)) return
      if (!chatMessageIds[items[0]] || !chatMessageIds[items[1]])
        getNextPage(type)
    },
    [canFetchNextPage, chatMessageIds, type]
  )

  if (chatMessageIds?.length === 0) {
    return (
      <div className="webinar-chat-no-messages" data-hidden={isHidden}>
        <FormattedMessage defaultMessage="No chat messages have been posted" />
      </div>
    )
  }

  return (
    <div
      ref={scrollContainer}
      className="webinar-chat-messages"
      data-hidden={isHidden}
    >
      {chatMessageIds && (
        // @ts-expect-error — should accept readonly items list
        <ViewportList
          viewportRef={scrollContainer}
          items={chatMessageIds}
          initialIndex={chatMessageIds.length - 1}
          overscan={3}
          axis="y"
          itemSize={350}
          onViewportIndexesChange={handleScroll}
        >
          {(commentId, idx) => (
            <ChatMessage
              key={`${commentId}-${idx}`}
              hash={idx}
              id={commentId}
            />
          )}
        </ViewportList>
      )}
    </div>
  )
}
export { ChatMessages }
